import { parseGridId } from '@/lib/utils';
import type { PlinthMaterialId } from '../theming/plinths/types';

export type MockAdapter = 'uniswap-lp' | 'megapot' | 'aerodrome' | 'flaunch' | 'custom' | 'yield-vault';
export type MockTaxToken = 'USDC' | 'WETH' | 'TAKEOVER';
export type MockSkybox =
  | 'aurora'
  | 'sci-fi'
  | 'medieval'
  | 'sky'
  | 'farm'
  | 'desert'
  // 1-of-1 cabal chamber reserved for the Boardroom (HV grid 0). Hidden
  // from the launch wizard picker via `tier: 'premium'` in the registry.
  | 'sanctum';

/**
 * Optional *board-level* plinth material — the slab the entire grid rests
 * on. Distinct from a player's `playerPlinth` (the cap under their seats).
 * When unset the board renders with the skybox's default material (see
 * `DEFAULT_MATERIAL_FOR_SKYBOX` in the theming registry). This field is
 * persisted in the `pool_metadata.board_plinth` column and populated by
 * the launch wizard's theming step.
 */
export type MockBoardPlinth = PlinthMaterialId;

export interface MockBoardHolder {
  address: string;
  name: string;
  level: number;
  avatarUrl?: string | null;
  /** Seats / tiles held on this board (live grids only; mocks use a fake gradient). */
  seatCount?: number;
}

export interface MockBoardSocials {
  website?: string;
  x?: string;
  telegram?: string;
  discord?: string;
}

export interface MockBoard {
  id: string;
  slug: string;
  name: string;
  tagline: string;
  /** Longer marketing description shown in the pinned title panel. */
  description: string;
  /**
   * Optional explicit cover image URL. When set, every consumer
   * (`PinnedTitlePanel`, `BoardroomCard`, `BoardCover`, etc.) renders
   * this image instead of the procedural mosaic fallback. The boardroom
   * uses `/mascot.png` until the v2 metadata pipeline allows
   * board owners to upload their own.
   */
  imageUrl?: string;
  socials: MockBoardSocials;
  gridSize: number;
  capacity: number;
  taken: number;
  taxToken: MockTaxToken;
  /** Floor price as a pre-formatted USD string (e.g. `$38`, `$1.2k`).
   *  The lobby always renders floors in USD so a casual viewer can
   *  compare boards without cross-token conversion. Never store ETH
   *  values here — convert upstream if needed. */
  floor: string;
  rewards7d: string;
  /** Cumulative tax revenue collected by the board, formatted as a USD
   *  string (e.g. `$1.2k`). Drives the lobby directory's default sort
   *  ("Taxes") and the `>$0.00 paid in tax` filter — boards are
   *  surfaced when players have actually paid Harberger tax on them
   *  rather than when raw yield has flowed in (yield-only boards
   *  with no holders read as "live" under the old rewards sort).
   *
   *  Sourced from `LobbyGridSummary.totalTaxCollected` (lifetime, raw
   *  token units) converted to USD via the tax token's price oracle.
   *  Once the indexer rolls up a windowed tax field (mirroring
   *  `yield7d`), `gridSummaryToMockBoard` should swap in the 7d-window
   *  value here so the sort reflects recent activity rather than
   *  cumulative history; the field name stays generic to absorb that
   *  switch without touching call sites. */
  taxesUsd: string;
  tvl: string;
  /** Mock/demo viewer counts; `null` when live presence is not tracked. */
  chatOnline: number | null;
  adapter: MockAdapter;
  yieldToken: string;
  /** `null` = the creator explicitly picked "None" in the launch wizard,
   *  so the board renders with no skybox (neutral void). Distinct from
   *  "no metadata yet": legacy boards still get an adapter-derived
   *  default in `gridSummaryToMockBoard` before they reach this type. */
  skybox: MockSkybox | null;
  /** Board-level plinth tri-state:
   *   - registered material id → render that slab;
   *   - `null` → explicit None, skip the plinth entirely (tiles float);
   *   - absent → no choice yet, canvas falls back to the skybox default.
   *  Mirrors the `pool_metadata.board_plinth` column; `playerPlinth`
   *  lives on the user profile instead. */
  boardPlinth?: MockBoardPlinth | null;
  hue: string;
  pinned?: boolean;
  premium?: boolean;
  ageDays: number;
  /** Precise creation timestamp in unix seconds. Distinct from `ageDays`,
   *  which floors to whole days and is therefore useless for the
   *  sub-day "is this board fresh?" decisions the lobby needs to make
   *  (the curated rail promotes any grid created in the last
   *  `NEW_BOARD_WINDOW_SECONDS` even if the curator hasn't allow-listed
   *  it yet, see `isBoardNew`). Sourced from
   *  `LobbyGridSummary.createdAt` for live grids; static fallbacks
   *  (Boardroom / tutorial stubs in `MOCK_BOARDS`) set this to `0` so
   *  they read as "ancient" and never accidentally light up the NEW
   *  pill before live data arrives. */
  createdAtSec: number;
  /**
   * Per-week Harberger tax rate in basis points. Forwarded from
   * `LobbyGridSummary.taxRateBps` so the metadata panel can display
   * the board's actual tax rate (creators pick anywhere from 1%–10%
   * in the launch wizard, not the legacy 5% default). `undefined`
   * for static fallbacks / mock boards where there's no live grid to
   * read from — `BoardMetadataSnapshot` simply skips the chip in
   * that case.
   */
  taxRateBps?: number;
  /**
   * UGM contract version this board is deployed on:
   *   - `'v2.2'` → seats forfeit into a Dutch auction at the holder's
   *     last self-assessed price, decaying linearly to 0 over the
   *     grid's `forfeitureDuration`. Protocol takes a 5% penalty on
   *     the clearing price.
   *   - `'v2.1'` → seats become instantly free-claim on forfeit. The
   *     legacy default (and the on-chain shape of the Boardroom).
   * Forwarded from `LobbyGridSummary.gridVersion` so the
   *  `BoardMetadataSnapshot` can render the right "forfeit" chip and
   *  the FAQ has something to point at. Anything the indexer doesn't
   *  positively tag as `'v2.2'` collapses to `'v2.1'` upstream — the
   *  lobby renderers don't need to second-guess that.
   */
  gridVersion?: 'v2.1' | 'v2.2';
  /**
   * Distinct addresses currently holding at least one seat on the
   * board. Forwarded from `LobbyGridSummary.uniqueHolderCount` so
   * the lobby's composite "Activity" sort and the
   * `uniqueHolderCount > 0` half of the visibility filter can both
   * read it without re-deriving from `seats[]`. Defaults to `0` for
   * static fallbacks (mocks / placeholder) — they read as "no
   * holders" until live data arrives, which is the correct
   * deactivated state.
   */
  uniqueHolderCount: number;
  /**
   * Unix-seconds timestamp of the most recent seat purchase on this
   * board, or `null` when no seat has ever been bought. Powers the
   * "recency" axis of the Activity sort: a board with high lifetime
   * tax but no recent buys decays out of the top of the list while
   * a freshly-active board with modest cumulative numbers rises.
   * Mirrors `LobbyGridSummary.lastSeatPurchasedAt` shape (also unix
   * seconds, `null` when absent).
   */
  lastSeatPurchasedAtSec: number | null;
  /**
   * Trailing-7d count of non-deleted chat messages posted to this
   * board's chat room. Server-aggregated from `chat_messages` in
   * `getMessageCountsByRoomSince` and joined onto the lobby payload
   * post-cache (so a busy chat isn't pinned to a stale value). Used
   * for the standalone "Chat" sort and as a third dimension of the
   * Activity composite — directly captures the "people pay tax to
   * be in chat with no yield flowing" success case the rewards-only
   * sort missed entirely. Defaults to `0` for static fallbacks.
   */
  chatMessages7d: number;
  /**
   * Lowercased grid creator address. Forwarded from
   * `LobbyGridSummary.creator` so the lobby's "My Boards" tab can
   * surface boards the viewer launched even when they hold no seats
   * on them — without this, a creator who hasn't bought back any of
   * their own primary-mint inventory would never see the board they
   * just deployed in their own portfolio rail. Static fallbacks
   * (Boardroom / tutorial mocks) leave it `undefined`.
   */
  creatorAddress?: string;
  topHolders: MockBoardHolder[];
}

/** How long after creation a board still counts as "NEW" for the lobby's
 *  curated rail and the NEW pill on the rail card.
 *
 *  4 hours is short enough that the badge stays meaningful (it actually
 *  signals "just launched, go look") and long enough that a board
 *  created during off-peak hours still gets a UTC-day's worth of prime-
 *  time exposure on the curated rail before it falls out. The user
 *  asked for "less than 4 hours old" verbatim. */
export const NEW_BOARD_WINDOW_SECONDS = 4 * 60 * 60;

/** True iff `board` was created within the NEW window relative to
 *  `nowSec`. `nowSec` is passed in (not read off `Date.now()`) so all
 *  consumers in a single render — the curated/community bucketing
 *  inside `useLobbyBoards` and the per-card NEW pill in `BoardCard` —
 *  decide on the same clock and never disagree about whether a board
 *  on the boundary is fresh. The boardroom static fallback uses
 *  `createdAtSec === 0`, which is always "ancient" relative to any
 *  realistic now and therefore never trips the badge. */
export function isBoardNew(
  board: Pick<MockBoard, 'createdAtSec'>,
  nowSec: number,
): boolean {
  if (!board.createdAtSec || board.createdAtSec <= 0) return false;
  const ageSec = nowSec - board.createdAtSec;
  return ageSec >= 0 && ageSec < NEW_BOARD_WINDOW_SECONDS;
}

/**
 * Sentinel value written into `MockBoard.floor` (and any other USD-typed
 * field) when the upstream price oracle hasn't yet resolved a USD figure
 * for the underlying token amount. Consumers MUST detect this string and
 * render a loading placeholder (skeleton / dash) rather than feeding it
 * to a number formatter — earlier code path treated the raw token
 * amount (e.g. `"24801.3k TAKEOVER"`) as a USD number and produced the
 * nonsense `$24801.3k TAKEOVER` headline because `formatBoardFloor`'s
 * defensive `$`-prefix kicked in. Keeping this as an opaque
 * underscore-bracketed string keeps it impossible to confuse with any
 * real `formatMarketUsd` output.
 */
export const FLOOR_PENDING_USD = '__pending_usd__';

/**
 * Returns true iff the given floor string is the "USD value pending"
 * sentinel emitted by `gridSummaryToMockBoard` when the TAKEOVER/ETH
 * price hasn't loaded yet. Helpers in `formatBoardFloor` and the
 * lobby card / sort comparators short-circuit on this check.
 */
export function isFloorPending(floor: string): boolean {
  return floor === FLOOR_PENDING_USD;
}

/**
 * Defensive formatter for `MockBoard.floor` — always returns a USD-prefixed
 * string for resolved values. The lobby's render contract is now USD-only
 * for every convertible tax token (USDC / TAKEOVER / WETH/ETH); raw
 * token amounts are filtered upstream into `FLOOR_PENDING_USD`. Returns
 * the sentinel as-is so callers can branch on it via `isFloorPending`
 * before rendering.
 */
export function formatBoardFloor(floor: string): string {
  if (isFloorPending(floor)) return floor;
  const trimmed = floor.trim();
  if (trimmed.startsWith('$')) return trimmed;
  // Defensive `$?` for legacy mixed-unit strings — the lobby never
  // surfaces ETH/wei labels today, but keep the guard so a future
  // regression in the upstream formatter shows obviously instead of
  // silently looking like a real USD figure.
  if (/[Ξ]|eth|wei/i.test(trimmed)) return '$?';
  // Anything else (e.g. `"123 AERO"` for tokens with no USD oracle) is
  // returned verbatim so the unit suffix stays attached. Previously we
  // auto-prepended `$` to anything starting with a digit, which is what
  // produced the broken `$24801.3k TAKEOVER` headline when live data
  // arrived before the TAKEOVER price oracle resolved.
  return trimmed;
}

const NAME_BANK = [
  'kingmaker', 'legend.eth', 'recruiter', 'dragonborn', 'oracle.eth',
  'pot.king', 'megadroid', 'soakmode', 'fortuna', 'bigbang',
  'chad.eth', 'farside', 'meme.lord', 'silentbob', 'hydra',
  'frost', 'stormcall', 'zenith', 'gusty', 'talos',
  'dashiell', 'ticketmaster', 'houseedge', 'fortune', 'toadstool',
  'truffle.hog', 'gardener', 'petalpusher', 'apollo.eth', 'luna.ox',
  'void.walker', 'ember', 'nova', 'glint', 'onyx',
  'ironclad', 'paladin', 'seer', 'reaper', 'jester',
  'marrow', 'pine', 'sable', 'vellum', 'amberstone',
  'halcyon', 'cinder', 'sombra', 'quill', 'bastion',
];

function mkAddress(seed: number): string {
  // Deterministic pseudo-address generator for the boardroom fallback
  // holders list (used when live data hasn't streamed in yet). Uses
  // xorshift32 + a high nibble to avoid LCG low-bit cycling that would
  // produce repeated 16-char patterns and collisions.
  let s = (seed >>> 0) || 1;
  let out = '0x';
  for (let i = 0; i < 40; i++) {
    s ^= s << 13;
    s ^= s >>> 17;
    s ^= s << 5;
    s = s >>> 0;
    out += ((s >>> 16) & 0xf).toString(16);
  }
  return out;
}

function buildHolders(seedBase: number, count: number, levelBias = 3): MockBoardHolder[] {
  const out: MockBoardHolder[] = [];
  for (let i = 0; i < count; i++) {
    const seed = (seedBase * 131) ^ (i * 977 + 13);
    const rng = Math.abs(Math.sin(seed) * 10000) % 1;
    const nameIndex = (seedBase + i * 17) % NAME_BANK.length;
    // Sorted roughly by level — top holders come first
    const baseLevel = Math.max(1, Math.min(6, Math.round(levelBias + (1 - i / count) * 3 + (rng - 0.5))));
    const seatCount = Math.max(1, Math.round(48 - i * 1.8 + (rng - 0.5) * 6));
    out.push({
      address: mkAddress(seedBase * 9001 + i * 7),
      name: NAME_BANK[nameIndex],
      level: baseLevel,
      seatCount,
    });
  }
  return out;
}

/**
 * Boardroom-only fallback registry.
 *
 * Historically this list held ~10 decorative "demo" boards for the
 * lobby screenshots. Those were removed once the live grid feed
 * (`useLobbyBoards()` → `useGrids()`) became the source of truth for
 * every community / curated card.
 *
 * The single remaining entry is the flagship **Boardroom** — kept as a
 * pre-fetch stub so the pinned card never flashes empty while
 * `useGrids()` is in flight or when the HV env vars aren't wired
 * (early SSR / unit tests). The live data is overlaid on top of this
 * stub in `use-lobby-boards.ts::brandBoardroomOverlay`.
 */
export const MOCK_BOARDS: MockBoard[] = [
  {
    id: 'boardroom',
    slug: 'boardroom',
    name: 'The Boardroom',
    // Static stub values used only for the brief pre-fetch window
    // before `useLobbyBoards()` overlays the live row via
    // `brandBoardroomOverlay`. Both tagline and description are kept
    // identical to what the live overlay would emit (seat-count
    // tagline matching `taglineFromGrid`; canonical fallback
    // description matching `gridSummaryToMockBoard`) so the pinned
    // card never flashes a different tagline / tooltip copy at first
    // paint than it settles on once live data arrives.
    tagline: '256 / 256 seats',
    description:
      "Land holders get access to rewards from the board's Yield and private group chat.",
    // The pixel-art boardroom portrait is the canonical visual identity
    // for the Boardroom AS a grid. Mirrors what `resolveGridDisplay()`
    // returns for HV grid 0, so every `BoardCover` consumer (mobile
    // rail, metadata snapshot, notifications) lights up with the same
    // image — both during the SSR fallback pass and after live data
    // arrives, and stays in lock-step with `BoardroomIcon` /
    // `BoardroomCard` / the boardroom explainer modal.
    imageUrl: '/boardroom.png',
    socials: {
      website: 'https://takeover.fun',
      x: 'https://x.com/takeoverfun',
      telegram: 'https://t.me/takeoverfun',
    },
    // 16×16 mirrors the on-chain HV grid 0 (256 seats). Used as the
    // pre-load fallback so the canvas renders the right footprint
    // before `useLobbyBoards()` overlays the live counts on top.
    gridSize: 16,
    capacity: 256,
    taken: 256,
    taxToken: 'TAKEOVER',
    // `FLOOR_PENDING_USD` rather than a hardcoded `$148` so the
    // `BoardroomCard` skeleton fires on cold load until
    // `useLobbyBoards()` overlays the live row. The previous stub
    // flashed `$148` for the ~200ms between first paint and
    // `useGrids()` + `useTakeoverPrice()` resolving, then snapped to
    // whatever the live TAKEOVER/USD conversion produced (e.g.
    // `$91.43`) — read as "why did the price just change?" by
    // anyone glancing at the card during hydration. `BoardroomCard`
    // already branches on `isFloorPending(board.floor)` and renders
    // a pulse bar, so flipping the stub to the sentinel is all it
    // takes: no new UI, no new conversion path. Rewards7d / taxesUsd
    // / tvl below are NOT flipped because the current `BoardroomCard`
    // layout only renders floor + seats — those other figures are
    // kept as decorative fallbacks for BoardTooltip (which doesn't
    // reference them today) and future card variants.
    floor: FLOOR_PENDING_USD,
    rewards7d: '$42.1k',
    taxesUsd: '$23.6k',
    tvl: '$612k',
    uniqueHolderCount: 24,
    lastSeatPurchasedAtSec: null,
    chatMessages7d: 0,
    chatOnline: 28,
    adapter: 'uniswap-lp',
    yieldToken: 'USDC',
    // 1-of-1 cabal sky reserved for the Boardroom: black-void chamber
    // with a slow rotating crimson sigil overhead, indigo incense, and
    // a smouldering ember horizon. Paired with the 1-of-1 emerald
    // velvet drape (gold tassels at the corners) so the chamber reads
    // as cabal-regal rather than just dark — both `skybox: 'sanctum'`
    // and `boardPlinth: 'velvet'` are `tier: 'premium'` and filtered
    // out of community-facing pickers.
    skybox: 'sanctum',
    boardPlinth: 'velvet',
    hue: '#ef4444',
    pinned: true,
    premium: true,
    ageDays: 182,
    // Static fallbacks pin `createdAtSec` to 0 (unix epoch) so the
    // "is this board < 4h old?" check in `isBoardNew` always reads
    // them as ancient. The live boardroom row replaces this in
    // `brandBoardroomOverlay` once `useGrids()` resolves; until then
    // the pinned card never accidentally flashes a NEW pill on the
    // 6-month-old flagship.
    createdAtSec: 0,
    topHolders: buildHolders(1, 24, 5),
  },
  {
    id: 'tutorial',
    slug: 'tutorial',
    name: 'Tutorial Town',
    tagline: 'Sandbox board · learn the loop · no wallet needed',
    description:
      'A 10x10 sandbox board with 100 simulated holders. Walk through the takeover loop end-to-end without spending a cent or signing a single transaction.',
    socials: {},
    gridSize: 10,
    capacity: 100,
    taken: 100,
    taxToken: 'USDC',
    floor: '$5',
    rewards7d: '$48',
    taxesUsd: '$72',
    tvl: '$359k',
    uniqueHolderCount: 12,
    lastSeatPurchasedAtSec: null,
    chatMessages7d: 0,
    chatOnline: null,
    adapter: 'uniswap-lp',
    yieldToken: 'USDC',
    skybox: 'aurora',
    boardPlinth: 'obsidian',
    hue: '#3b82f6',
    pinned: false,
    premium: false,
    ageDays: 7,
    // See the boardroom entry — fixed at epoch so the static tutorial
    // stub never trips the NEW pill regardless of when the lobby boots.
    createdAtSec: 0,
    topHolders: buildHolders(7777, 12, 4),
  },
];

export function adapterLabel(a: MockAdapter): string {
  switch (a) {
    case 'uniswap-lp': return 'Uniswap LP';
    case 'megapot': return 'Megapot';
    case 'aerodrome': return 'Aerodrome';
    case 'flaunch': return 'Flaunch';
    case 'custom': return 'Custom';
    case 'yield-vault': return 'Yield Vault';
  }
}

/**
 * Neutral `MockBoard` for an active board id we don't have data for yet.
 *
 * Used by `LobbyClient` as the last-resort fallback when an `0x…-N`
 * board id resolves before either `useLobbyBoards()` (which builds a
 * branded `MockBoard` per live grid) or `useGridPage()` (which has the
 * per-grid metadata) returns. The previous fallback to `MOCK_BOARDS[0]`
 * (Boardroom) silently inherited the flagship's skybox / plinth / hue /
 * image / description, so a brand-new board's first paint flashed as
 * "The Boardroom" until the lobby list arrived — and stayed that way
 * permanently when the grid hadn't yet been picked up by `/api/v1/grids`
 * (e.g. assetless newly-created grids before the `v3` lobby filter
 * was relaxed; see `app/api/v1/grids/route.ts::buildLobbyGrids`).
 *
 * The returned shape is intentionally identity-neutral: no name (Grid
 * #N is rendered downstream from the id), no skybox, no plinth, no
 * description. Consumers that want richer visuals layer their own data
 * on top via the `activeBoard` merge in `LobbyClient.tsx`.
 */
export function makePlaceholderBoard(
  id: string,
  options: { gridSize?: number } = {},
): MockBoard {
  const gridSize = options.gridSize ?? 10;
  // Use the grid's numeric id (`Grid #3`) as a placeholder display
  // name so the title chip renders something meaningful before the
  // creator's wizard-set name lands. `parseGridId` returns the
  // composite id verbatim for non-`0x` ids (e.g. dev mocks), which
  // produces a slightly ugly but never-broken fallback.
  const { numericId } = parseGridId(id);
  const placeholderName = numericId ? `Grid #${numericId}` : id;
  return {
    id,
    slug: id,
    name: placeholderName,
    tagline: '',
    description: '',
    socials: {},
    gridSize,
    capacity: gridSize * gridSize,
    taken: 0,
    taxToken: 'USDC',
    // `FLOOR_PENDING_USD` rather than `$0` so the `BoardCard`
    // skeleton fires while the placeholder is live — a newly-created
    // V2 grid that hits the lobby before `/api/v1/grids` picks it up
    // would otherwise flash `$0` on its card for the window between
    // mount and `useLobbyGridPage` resolving, then snap to the real
    // floor. `$0` is the shape of a vacant-floor display and
    // misleads viewers into thinking seats are free.
    floor: FLOOR_PENDING_USD,
    rewards7d: '$0',
    taxesUsd: '$0',
    tvl: '$0',
    uniqueHolderCount: 0,
    lastSeatPurchasedAtSec: null,
    chatMessages7d: 0,
    chatOnline: null,
    adapter: 'custom',
    yieldToken: 'USDC',
    // `null` here means "no skybox" rather than the flagship's
    // `'sanctum'` — let the canvas + theming registry render a neutral
    // void until real metadata lands. Same reasoning for `boardPlinth`.
    skybox: null,
    boardPlinth: null,
    hue: '#ffba49',
    pinned: false,
    premium: false,
    ageDays: 0,
    createdAtSec: 0,
    topHolders: [],
  };
}
